import { useEffect, useRef, useState } from 'react'
import {
  GetManagementActivity, GetManagementOverview, GetManagementStatus
} from '../services/peerToPeer/ManagementService'
import {
  type ManagementActivity, type ManagementBuildingRow, type ManagementDrillLevel,
  type ManagementOverview, type ManagementStatusFilter
} from '../models/peerToPeer/Management'
import { handleApiResponse } from '../utils/apiHelpers'
import useDebounce from './use-debounce'

const SEARCH_DEBOUNCE_MS = 300

interface UseP2PManagementArgs {
  view: 'org' | 'status'
  statusFilter: ManagementStatusFilter
  search: string
  filterLevel: ManagementDrillLevel
}

interface UseP2PManagementType {
  overview: ManagementOverview | null
  yearId: number
  setYearId: (yearId: number) => void
  activity: ManagementActivity | null
  statusRows: ManagementBuildingRow[]
  /** True once the debounced query is non-empty: results come from the flat form table, not the drill-down. */
  searching: boolean
  refresh: () => void
}

export const useP2PManagement = ({ view, statusFilter, search, filterLevel }: UseP2PManagementArgs): UseP2PManagementType => {
  const [overview, setOverview] = useState<ManagementOverview | null>(null)
  const [yearId, setYearId] = useState<number>(0)
  const [activity, setActivity] = useState<ManagementActivity | null>(null)
  const [statusRows, setStatusRows] = useState<ManagementBuildingRow[]>([])
  const [refreshKey, setRefreshKey] = useState(0)
  const loadedOverviewYear = useRef<number | null>(null)
  const latestRequest = useRef(0)

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS)
  const searching = debouncedSearch.trim() !== ''

  useEffect(() => {
    if (loadedOverviewYear.current === yearId) return

    const loadOverview = async (): Promise<void> => {
      const result = await handleApiResponse(GetManagementOverview(yearId))
      if (result == null) return
      // Year 0 asks the server for the active year; adopt the id it answers with so the
      // drill-down and form queries below name a concrete year.
      const resolvedYear = yearId === 0 ? (result.yearOptions[0]?.id ?? 0) : yearId
      loadedOverviewYear.current = resolvedYear
      setOverview(result)
      if (resolvedYear !== yearId) setYearId(resolvedYear)
    }

    void loadOverview()
  }, [yearId])

  useEffect(() => {
    if (yearId === 0) return
    latestRequest.current += 1
    const requestId = latestRequest.current
    const isCurrent = (): boolean => requestId === latestRequest.current

    const run = async (): Promise<void> => {
      // A search query (or the Status view) shows a flat form-results table; otherwise the
      // Organization view shows the accordion drill-down at the filtered level.
      if (view === 'status' || searching) {
        const rows = await handleApiResponse(GetManagementStatus(statusFilter, debouncedSearch, yearId)) ?? []
        if (isCurrent()) setStatusRows(rows)
      } else {
        const result = await handleApiResponse(GetManagementActivity(filterLevel, yearId)) ?? null
        if (isCurrent()) setActivity(result)
      }
    }

    void run()
  }, [yearId, view, statusFilter, debouncedSearch, searching, filterLevel, refreshKey])

  return {
    overview,
    yearId,
    setYearId,
    activity,
    statusRows,
    searching,
    refresh: () => { setRefreshKey(key => key + 1) }
  }
}
